"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick, tabSwitched } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Cards 1053 — KUK home "Featured Programmes".
 *
 * Port of kuk-campus-portal `components/Home/FeaturedProgrammes.tsx`: a ruled
 * heading, a Bachelors / Masters / Diploma tab strip, and a two-pane body — a
 * grid of selectable programme cards on the left, the selected programme's
 * detail card on the right, and its specialisations in a grid underneath.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px).
 *
 * ONE ROW TYPE, TWO ROLES. v1 reads live programme records with their
 * specialisations nested inside them. A CMS `metadata` array is flat, so a row
 * with an empty `meta_parent_title` is a PROGRAMME (it appears in the list, the
 * detail card and the tab strip) and a row that names one is a SPECIALISATION of
 * the programme whose `meta_title` matches. That is the convention Program Cards
 * 1027 already uses, and it needs no new column.
 *
 * TABS COME FROM THE DATA. v1 hardcodes a `{1: Bachelors, 2: Masters, 3: Diploma}`
 * map keyed on a numeric `program_type_id`, renders `[1,2,3]` panels
 * unconditionally and puts a white divider after any tab whose index is under 2 —
 * so a two-tab strip still gets a trailing divider. Here the tabs are the
 * distinct `meta_category` values in row order, dividers go strictly BETWEEN
 * tabs, and no empty panel is emitted.
 *
 * SELECTION ON TAB SWITCH — v1 selects the first programme of the newly active
 * level, which is reproduced. It does this even when that level is empty, which
 * leaves the previous level's detail card on screen; here an empty level shows
 * the reference's "No Programs Available" line instead.
 *
 * THE MOBILE SPECIALISATION CHIPS — v1 labels each chip by taking the LAST word
 * of the parent's short name and everything after the first literal "in" in the
 * specialisation's display name, so "Online MA" + "Master Of Arts in English"
 * becomes "MA English". A specialisation whose name has no " in " renders half
 * blank. Here the chip is the parent's last word plus the specialisation's own
 * short title, which is the same output from data that cannot misparse.
 *
 * THE BROCHURE BUTTON — v1 downloads directly for a verified visitor and opens
 * the sign-up form for everyone else. Verification is per-visitor session state,
 * so this always opens the lead popup in its DOWNLOAD variant with the row's
 * brochure attached, which is this app's equivalent of the same flow.
 */

/** v1's `.custom-underline`: `bottom:-6px; left:0; width:33%; height:5px`. */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

/** v1 truncation lengths, on the detail description and the specialisation cards. */
const DETAIL_TRUNCATE = 150;
const SPECIALISATION_TRUNCATE = 40;

/** v1 hardcodes this icon on every card and on the detail header. */
const DEFAULT_ICON = "graduation";

interface ProgramRow {
  id?: string | number;
  /** Short name, e.g. "Online MA". Also the key specialisations point at. */
  meta_title?: string;
  /** Full name, e.g. "Master of Arts". */
  meta_sub_title?: string;
  meta_description?: string;
  meta_duration?: string;
  meta_image?: string;
  meta_alt_image?: string;
  meta_url?: string;
  meta_brochure_url?: string;
  /** Tab this row belongs under, e.g. "Bachelors". */
  meta_category?: string;
  /** Empty = a programme. Set = a specialisation of the programme it names. */
  meta_parent_title?: string;
  /** Icon-map key or an uploaded image URL. Empty uses the reference's icon. */
  meta_icon?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramCards1053Data {
  metadata?: ProgramRow[];
  /** Section heading. */
  title?: string;
  title_text_color?: string;
  /** Accent rule under the heading. */
  gradient_color_1?: string;
  /** Band behind the section. */
  desktop_background_color?: string;
  /** Tab strip fill. */
  mobile_background_color?: string;
  /** Active tab fill / text. */
  card_hover_background_color?: string;
  card_hover_text_color?: string;
  /** Inactive tab text. */
  title_start_color?: string;
  /** Programme card fill — unselected / selected. */
  card_background_color?: string;
  gradient_color_2?: string;
  /** Programme card text — unselected title / selected. */
  title_end_color?: string;
  gradient_color_3?: string;
  /** Programme card sub-line, and the detail description. */
  salary_text_color?: string;
  /** "N Specialisations" badge. */
  key_skills_background_color?: string;
  key_skills_text_color?: string;
  /** Detail card fill. */
  gradient_color_4?: string;
  /** Detail card eyebrow (the full programme name). */
  pre_title_color?: string;
  /** Specialisation chip fill. */
  key_skills_border_color?: string;
  /** "Specialisations" label above the mobile chips. */
  pre_title?: string;
  /** "Specializations" heading over the desktop grid. */
  description?: string;
  /** "Duration:" label. */
  note?: string;
  /** Know-more button. */
  cta_text?: string;
  cta_text_color?: string;
  /** Brochure button. */
  load_more_text?: string;
  load_more_desktop_text_color?: string;
  load_more_mobile_background_color?: string;
  load_more_desktop_icon?: string;
  status?: boolean;
}

function truncate(text: string | undefined, max: number): string {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max).trimEnd()}...`;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** v1's chip label: the last word of the parent's short name. */
function lastWord(text: string | undefined): string {
  const parts = (text || "").trim().split(/\s+/);
  return parts[parts.length - 1] || "";
}

const ProgramCards1053 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as ProgramCards1053Data | undefined;
  const dispatch = useDispatch();

  const {
    title = "",
    title_text_color = "#031F44",
    gradient_color_1 = "#9F1C33",
    desktop_background_color = "#FFFFFF",
    mobile_background_color = "#F0F2F5",
    card_hover_background_color = "#9F1C33",
    card_hover_text_color = "#FFFFFF",
    title_start_color = "#031F44",
    card_background_color = "#FFFFFF",
    gradient_color_2 = "#9F1C33",
    title_end_color = "#031F44",
    gradient_color_3 = "#FFFFFF",
    salary_text_color = "#344054",
    key_skills_background_color = "#FFD964",
    key_skills_text_color = "#031F44",
    gradient_color_4 = "#FEE2E2",
    pre_title_color = "#9F1C33",
    key_skills_border_color = "#9F1C3333",
    pre_title = "Specialisations",
    description = "Specializations",
    note = "Duration:",
    cta_text = "Know more",
    cta_text_color = "#031F44",
    load_more_text = "Brochure",
    load_more_desktop_text_color = "#FFFFFF",
    load_more_mobile_background_color = "#9F1C33",
    load_more_desktop_icon = "download",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const rows = useMemo(() => {
    const all = Array.isArray(sectionData?.metadata) ? sectionData!.metadata! : [];
    return all
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const programmes = useMemo(
    () => rows.filter((r) => !(r.meta_parent_title || "").trim()),
    [rows],
  );

  /** Specialisations keyed by the parent short name they name. */
  const specialisationsByParent = useMemo(() => {
    const map = new Map<string, ProgramRow[]>();
    rows
      .filter((r) => (r.meta_parent_title || "").trim())
      .forEach((r) => {
        const key = (r.meta_parent_title || "").trim();
        map.set(key, [...(map.get(key) || []), r]);
      });
    return map;
  }, [rows]);

  /** Tabs are the distinct categories in row order — no fixed level list. */
  const categories = useMemo(() => {
    const seen: string[] = [];
    programmes.forEach((p) => {
      const c = (p.meta_category || "").trim();
      if (c && !seen.includes(c)) seen.push(c);
    });
    return seen;
  }, [programmes]);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedId, setSelectedId] = useState<string>("");

  const activeCategory = categories[activeTab] || "";
  const tabProgrammes = useMemo(
    () =>
      categories.length === 0
        ? programmes
        : programmes.filter((p) => (p.meta_category || "").trim() === activeCategory),
    [programmes, categories.length, activeCategory],
  );

  const rowKey = useCallback(
    (r: ProgramRow, i: number) => String(r.id ?? `${r.meta_title || ""}-${i}`),
    [],
  );

  // v1 selects the first programme of the active level, on mount and on switch.
  useEffect(() => {
    const first = tabProgrammes[0];
    setSelectedId(first ? rowKey(first, 0) : "");
  }, [activeTab, tabProgrammes, rowKey]);

  const selected = useMemo(
    () => tabProgrammes.find((p, i) => rowKey(p, i) === selectedId) || tabProgrammes[0],
    [tabProgrammes, selectedId, rowKey],
  );

  const selectedSpecialisations = useMemo(
    () => specialisationsByParent.get((selected?.meta_title || "").trim()) || [],
    [specialisationsByParent, selected],
  );

  const handleBrochureClick = useCallback(() => {
    if (!selected) return;
    ctaClick({
      section: "program_cards_1053",
      component: "download_brochure",
      label: load_more_text?.trim() || "brochure",
      step: "1",
      entity: selected.meta_sub_title || selected.meta_title || "",
      parentEntity: "program_cards_1053",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: selected.meta_brochure_url || "",
      }),
    );
  }, [dispatch, load_more_text, selected]);

  if (!status || programmes.length === 0) return null;

  const detailSpecCount = selectedSpecialisations.length;

  return (
    <section className="w-full" style={{ backgroundColor: desktop_background_color }}>
      <div className="relative mx-auto w-full max-w-[1120px] overflow-hidden">
        <div className="w-full p-10 tab:p-20">
          {title && (
            <h2
              className="relative w-fit text-2xl font-bold tab:text-4xl"
              style={{ color: title_text_color }}
            >
              {title}
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: gradient_color_1 }}
              />
            </h2>
          )}

          <div className="mx-auto mt-10 w-full">
            {categories.length > 1 && (
              <div
                role="tablist"
                aria-label={title || "Programme levels"}
                className="mb-8 inline-flex h-[38px] w-full items-center justify-center rounded-lg p-0 tab:w-[415px]"
                style={{ backgroundColor: mobile_background_color }}
              >
                {categories.map((label, i) => {
                  const isActive = i === activeTab;
                  return (
                    <div key={label} className="contents">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => {
                          tabSwitched({
                            section: "program_cards_1053",
                            component: label,
                            label,
                          });
                          setActiveTab(i);
                        }}
                        className={cn(
                          "inline-flex w-1/2 items-center justify-center whitespace-nowrap rounded-md",
                          "px-3 py-2 text-sm font-medium transition-all",
                        )}
                        style={
                          isActive
                            ? {
                                backgroundColor: card_hover_background_color,
                                color: card_hover_text_color,
                              }
                            : { color: title_start_color }
                        }
                      >
                        {label}
                      </button>
                      {i < categories.length - 1 && (
                        <span aria-hidden className="h-full w-0.5 bg-white" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div
              className={cn(
                "flex flex-col items-center justify-between gap-6",
                "sm:flex-row",
              )}
            >
              {/* Programme list */}
              <div
                className={cn(
                  "grid w-full grid-cols-2 gap-6",
                  tabProgrammes.length <= 3
                    ? "tab:flex tab:w-80 tab:flex-col sm:ml-20"
                    : "tab:w-1/2",
                )}
              >
                {tabProgrammes.map((p, i) => {
                  const key = rowKey(p, i);
                  const isSelected = selected ? rowKey(selected, tabProgrammes.indexOf(selected)) === key : false;
                  const specCount = (
                    specialisationsByParent.get((p.meta_title || "").trim()) || []
                  ).length;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => setSelectedId(key)}
                      className={cn(
                        "relative flex h-32 max-h-[86px] w-full cursor-pointer items-center",
                        "rounded-lg py-2 pl-2.5 text-left shadow-md transition-colors",
                      )}
                      style={{
                        backgroundColor: isSelected
                          ? gradient_color_2
                          : card_background_color,
                      }}
                    >
                      {p.meta_image && (
                        <span
                          role="img"
                          aria-label={p.meta_alt_image || ""}
                          className="mr-4 hidden size-14 bg-cover bg-center tab:block"
                          style={{ backgroundImage: `url("${p.meta_image}")` }}
                        />
                      )}
                      <span className="block min-w-0 p-0">
                        <span
                          className="flex w-full items-center gap-2 text-sm font-bold"
                          style={{
                            color: isSelected ? gradient_color_3 : title_end_color,
                          }}
                        >
                          <span className="hidden tab:block">
                            <DynamicIcon
                              iconName={p.meta_icon || DEFAULT_ICON}
                              color={isSelected ? gradient_color_3 : title_end_color}
                              width="20"
                              height="20"
                            />
                          </span>
                          <span className="truncate">{p.meta_title}</span>
                        </span>
                        <span
                          className="mr-1 mt-1 block text-xs font-light"
                          style={{
                            color: isSelected ? gradient_color_3 : salary_text_color,
                          }}
                        >
                          {p.meta_sub_title}
                        </span>
                      </span>
                      {specCount > 0 && (
                        <span
                          className={cn(
                            "absolute right-0 top-0 rounded-bl-md rounded-tr-md px-2 py-1",
                            "text-[8px] tab:text-xs",
                          )}
                          style={{
                            backgroundColor: key_skills_background_color,
                            color: key_skills_text_color,
                          }}
                        >
                          {specCount} {pre_title}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Detail card */}
              {selected ? (
                <div
                  className={cn(
                    "relative flex w-full flex-col rounded-xl shadow-lg",
                    "tab:h-[300px] tab:min-h-[300px] tab:w-1/2 tab:flex-row",
                  )}
                  style={{ backgroundColor: gradient_color_4 }}
                >
                  {selected.meta_image && (
                    <>
                      <span
                        role="img"
                        aria-label={selected.meta_alt_image || ""}
                        className="h-[107px] w-full bg-cover bg-center tab:hidden"
                        style={{ backgroundImage: `url("${selected.meta_image}")` }}
                      />
                      <span
                        role="img"
                        aria-label={selected.meta_alt_image || ""}
                        className="hidden w-full bg-cover bg-center tab:block tab:w-1/3"
                        style={{ backgroundImage: `url("${selected.meta_image}")` }}
                      />
                    </>
                  )}
                  <div className="mr-2 w-full space-y-3 p-6 py-10 tab:w-2/3">
                    <p
                      className="text-xs font-bold"
                      style={{ color: pre_title_color }}
                    >
                      {selected.meta_sub_title}
                    </p>
                    <div className="flex w-full flex-row flex-nowrap items-center justify-start gap-2 p-0">
                      <DynamicIcon
                        iconName={selected.meta_icon || DEFAULT_ICON}
                        color={title_end_color}
                        width="24"
                        height="24"
                      />
                      <span
                        className="text-lg font-bold"
                        style={{ color: title_end_color }}
                      >
                        {selected.meta_title}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: salary_text_color }}>
                      {truncate(selected.meta_description, DETAIL_TRUNCATE)}
                    </p>

                    {detailSpecCount > 0 && (
                      <div className="py-2 tab:hidden">
                        <p style={{ color: title_end_color }}>{pre_title}</p>
                        <div className="grid grid-cols-2 gap-1 pt-1">
                          {selectedSpecialisations.map((s, si) => (
                            <Link
                              key={s.id ?? si}
                              href={normalizeHref(s.meta_url || "")}
                              className={cn(
                                "flex min-h-[37px] w-[118px] justify-start overflow-hidden",
                                "rounded-md p-2 text-xs",
                              )}
                              style={{
                                backgroundColor: key_skills_border_color,
                                color: title_end_color,
                              }}
                            >
                              {lastWord(selected.meta_title)}{" "}
                              <span className="ml-1 font-light">{s.meta_title}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.meta_duration && (
                      <p
                        className="mt-4 flex gap-2 text-sm font-semibold"
                        style={{ color: title_end_color }}
                      >
                        <span>{note}</span>
                        <span style={{ color: salary_text_color }}>
                          {selected.meta_duration}
                        </span>
                      </p>
                    )}

                    <div className="mt-6 flex w-full items-start justify-start gap-3 p-0">
                      {cta_text && (
                        <Link
                          href={normalizeHref(selected.meta_url || "")}
                          onClick={() =>
                            ctaClick({
                              section: "program_cards_1053",
                              component: "know_more",
                              label: selected.meta_sub_title || selected.meta_title || "",
                              step: "",
                              entity: selected.meta_sub_title || "",
                              parentEntity: "program_cards_1053",
                            })
                          }
                          className={cn(
                            "inline-flex h-10 w-1/2 items-center justify-center rounded-md border",
                            "bg-transparent px-3 text-sm font-medium shadow-sm sm:w-[132px]",
                          )}
                          style={{
                            color: cta_text_color,
                            borderColor: cta_text_color,
                          }}
                        >
                          {cta_text}
                        </Link>
                      )}
                      {load_more_text && (
                        <button
                          type="button"
                          onClick={handleBrochureClick}
                          className={cn(
                            "inline-flex h-10 w-1/2 items-center justify-center gap-2 rounded-md",
                            "px-3 text-sm font-medium shadow sm:w-[132px]",
                          )}
                          style={{
                            backgroundColor: load_more_mobile_background_color,
                            color: load_more_desktop_text_color,
                          }}
                        >
                          {load_more_desktop_icon && (
                            <DynamicIcon
                              iconName={load_more_desktop_icon}
                              color={load_more_desktop_text_color}
                              width="16"
                              height="16"
                            />
                          )}
                          {load_more_text}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p
                  className="flex w-full items-center justify-start"
                  style={{ color: salary_text_color }}
                >
                  No Programs Available
                </p>
              )}
            </div>

            {/* Specialisations grid — desktop only, as in the reference. */}
            {detailSpecCount > 0 && (
              <div className="mt-10 hidden tab:block">
                <p
                  className="mb-4 text-2xl font-semibold"
                  style={{ color: title_end_color }}
                >
                  {description}
                </p>
                <div className="grid gap-4 tab:grid-cols-2 sm:grid-cols-3">
                  {selectedSpecialisations.map((s, si) => (
                    <div
                      key={s.id ?? si}
                      className="flex h-full items-center gap-2 rounded-md py-2 pl-2.5 shadow-md"
                      style={{ backgroundColor: gradient_color_4 }}
                    >
                      {s.meta_image && (
                        <Image
                          src={s.meta_image}
                          alt={s.meta_alt_image || s.meta_title || ""}
                          width={70}
                          height={50}
                          loading="lazy"
                          className="mr-4 hidden h-[120px] object-cover tab:block"
                        />
                      )}
                      <div className="flex flex-col items-start justify-center gap-2">
                        <div className="mr-2 flex items-center gap-2">
                          <DynamicIcon
                            iconName={s.meta_icon || DEFAULT_ICON}
                            color={title_end_color}
                            width="20"
                            height="20"
                          />
                          <p
                            className="text-sm font-bold"
                            style={{ color: title_end_color }}
                          >
                            {selected?.meta_title}
                          </p>
                        </div>
                        <span
                          className="-mt-2 ml-7 text-xs font-bold"
                          style={{ color: pre_title_color }}
                        >
                          {s.meta_title}
                        </span>
                        <p className="text-sm" style={{ color: salary_text_color }}>
                          {truncate(s.meta_description, SPECIALISATION_TRUNCATE)}
                        </p>
                        <Link
                          href={normalizeHref(s.meta_url || "")}
                          onClick={() =>
                            ctaClick({
                              section: "program_cards_1053",
                              component: "know_more",
                              label: s.meta_sub_title || s.meta_title || "",
                              step: "",
                              entity: s.meta_sub_title || "",
                              parentEntity: "program_cards_1053",
                            })
                          }
                          className="pl-0 text-sm font-bold underline"
                          style={{ color: pre_title_color }}
                        >
                          {cta_text}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramCards1053;
